import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCircleDot, faListCheck, faFolderOpen, faTrash } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';
import RichText from '../RichText';
import Avatar from '../Avatar';
import ChatLines from './ChatLines';
import { dayText, timeText } from './meetingHelpers';
import '../../styles/meetings.css';

/**
 * What happened in a meeting, kept for everybody who was in it: when it ran, who
 * came and for how long, who was invited but did not, the chat, what was decided
 * (notes written afterwards by whoever ran it), and whether it was recorded
 * (recording is not built yet, so it always says "Not recorded").
 *
 * From here a follow-up task can be started: the form opens already filled in
 * with the meeting's name, its notes and its project.
 */

const RECORDING_LABELS = {
    none: 'Not recorded',
    recording: 'Recording…',
    processing: 'Recording is being prepared',
    ready: 'Recorded',
    failed: 'Recording failed'
};

const ROLE_LABELS = { organizer: 'Organizer', cohost: 'Co-host', attendee: 'Attendee', guest: 'Guest' };

// The same roles that may assign tasks to others (server/utils/taskScoping.js).
const CAN_ASSIGN = ['MANAGER', 'TEAM LEAD', 'ADMIN', 'HR'];

const minutesText = (seconds) => {
    const total = Math.max(0, Math.round(seconds / 60));
    if (total < 1) return 'under a minute';
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
};

const readRole = () => {
    try { return JSON.parse(localStorage.getItem('user'))?.role || ''; } catch { return ''; }
};

const MeetingRecordModal = ({ record, onClose }) => {
    const navigate = useNavigate();
    const [tab, setTab] = useState('people');
    const [notes, setNotes] = useState(record.notes || []);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const meetingId = record.meeting._id;

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const chatLines = record.messages.length;

    const addNote = async (e) => {
        e.preventDefault();
        if (!draft.trim() || saving) return;
        setSaving(true);
        try {
            const res = await api.post(`/meetings/${meetingId}/notes`, { content: draft });
            setNotes(res.data);
            setDraft('');
        } catch (err) {
            Swal.fire('Could not save', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const removeNote = async (note) => {
        try {
            const res = await api.delete(`/meetings/${meetingId}/notes/${note._id}`);
            setNotes(res.data);
        } catch (err) {
            Swal.fire('Could not remove it', err.response?.data?.message || 'Please try again.', 'error');
        }
    };

    /**
     * A task for whatever has to happen next. People who may assign tasks get the
     * normal Add Task form; everybody else gets the request form on their own
     * task board. Either way it opens already started and nothing is created
     * until they press save there.
     */
    const startFollowUp = () => {
        const followUp = {
            title: `Follow-up: ${record.meeting.title}`,
            description: notes.length
                ? `From the meeting "${record.meeting.title}":\n\n${notes.map((n) => n.content).join('\n\n')}`.slice(0, 3000)
                : `Follow-up from the meeting "${record.meeting.title}".`,
            projectId: record.project?._id || ''
        };
        navigate(CAN_ASSIGN.includes(readRole()) ? '/add-task' : '/my-tasks', { state: { followUp } });
        onClose();
    };

    return (
        <div className="mt-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="mt-modal mt-record" role="dialog" aria-modal="true" aria-label={`Record of ${record.meeting.title}`}>
                <div className="mt-modal-head">
                    <div>
                        <h3>{record.meeting.title}</h3>
                        <span className="mt-subtle">
                            {record.startedAt
                                ? `${dayText(record.startedAt, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
                                : 'Meeting record'}
                            {record.project && <> · <FontAwesomeIcon icon={faFolderOpen} /> {record.project.name}</>}
                        </span>
                    </div>
                    <button type="button" className="mt-icon-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="mt-record-stats">
                    <div><span>Started</span><strong>{record.startedAt ? timeText(record.startedAt) : '-'}</strong></div>
                    <div><span>Ended</span><strong>{record.endedAt ? timeText(record.endedAt) : 'Still going'}</strong></div>
                    <div><span>Length</span><strong>{record.startedAt ? minutesText(record.durationSec) : '-'}</strong></div>
                    <div><span>People</span><strong>{record.attended.length}</strong></div>
                    <div className={`mt-recording ${record.recording.status}`}>
                        <span>Recording</span>
                        <strong><FontAwesomeIcon icon={faCircleDot} /> {RECORDING_LABELS[record.recording.status] || 'Not recorded'}</strong>
                    </div>
                </div>

                <div className="mt-record-bar">
                    <div className="mt-tabs mt-record-tabs" role="tablist">
                        <button type="button" className={tab === 'people' ? 'on' : ''} onClick={() => setTab('people')}>
                            People ({record.attended.length})
                        </button>
                        <button type="button" className={tab === 'chat' ? 'on' : ''} onClick={() => setTab('chat')}>
                            Chat ({chatLines})
                        </button>
                        <button type="button" className={tab === 'notes' ? 'on' : ''} onClick={() => setTab('notes')}>
                            Notes ({notes.length})
                        </button>
                    </div>
                    <button type="button" className="mt-btn ghost small" onClick={startFollowUp}>
                        <FontAwesomeIcon icon={faListCheck} /> Create follow-up task
                    </button>
                </div>

                <div className="mt-modal-body">
                    {tab === 'people' && (
                        <>
                            {record.attended.length === 0 && <p className="mt-subtle">Nobody joined.</p>}
                            <ul className="mt-people mt-record-people">
                                {record.attended.map((a, i) => (
                                    <li key={`${a.name}-${i}`}>
                                        <Avatar name={a.name} className="mt-mini-avatar" />
                                        <div className="mt-person">
                                            <span>{a.name}{a.connected ? ' · in the room' : ''}</span>
                                            <small>
                                                {ROLE_LABELS[a.role] || 'Attendee'}
                                                {' · '}joined {timeText(a.joinedAt)}
                                                {a.leftAt && !a.connected ? `, left ${timeText(a.leftAt)}` : ''}
                                                {a.rejoinCount > 0 ? ` · rejoined ${a.rejoinCount}×` : ''}
                                                {a.device ? ` · ${a.device}` : ''}
                                            </small>
                                        </div>
                                        <span className="mt-record-time">{minutesText(a.durationSec)}</span>
                                    </li>
                                ))}
                            </ul>

                            {record.notAttended.length > 0 && (
                                <div className="mt-section">
                                    <h4>Invited, did not join ({record.notAttended.length})</h4>
                                    <ul className="mt-history">
                                        {record.notAttended.map((p, i) => (
                                            <li key={`${p.name}-${i}`}>
                                                <span>{p.name}{p.isExternal ? ' (guest)' : ''}</span>
                                                <small>{p.rsvp === 'declined' ? 'Declined' : p.rsvp === 'accepted' ? 'Said yes' : p.rsvp === 'tentative' ? 'Said maybe' : 'No reply'}</small>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}

                    {tab === 'chat' && (
                        <div className="mt-record-chat">
                            {chatLines === 0 && <p className="mt-subtle">Nobody wrote in the chat.</p>}
                            <ChatLines messages={record.messages} withTime />
                        </div>
                    )}

                    {tab === 'notes' && (
                        <div className="mt-notes">
                            {notes.length === 0 && (
                                <p className="mt-subtle">
                                    {record.canWriteNotes ? 'Nothing written yet. Add what was decided.' : 'Nobody has written notes for this meeting.'}
                                </p>
                            )}
                            {notes.map((n) => (
                                <article key={n._id} className="mt-note">
                                    <header>
                                        <strong>{n.authorName}</strong>
                                        <small>{dayText(n.createdAt, { day: 'numeric', month: 'short' })}, {timeText(n.createdAt)}</small>
                                        {n.mine && (
                                            <button type="button" className="mt-icon-btn danger" onClick={() => removeNote(n)} aria-label="Delete this note" title="Delete this note">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </header>
                                    <RichText text={n.content} />
                                </article>
                            ))}

                            {record.canWriteNotes && (
                                <form className="mt-note-form" onSubmit={addNote}>
                                    <RichTextEditor
                                        rows={4}
                                        value={draft}
                                        maxLength={5000}
                                        placeholder="What was decided? Who does what next?"
                                        onChange={setDraft}
                                    />
                                    <button type="submit" className="mt-btn primary" disabled={saving || !draft.trim()}>Add note</button>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeetingRecordModal;
